import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  CircularProgress,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  ArrowBack,
  Image,
  PhotoCamera,
  CancelOutlined,
  Memory,
  Person,
  Block,
  CheckCircleOutlineOutlined,
  ExpandMore,
  RadioButtonUnchecked,
  PictureAsPdf,
  Close,
  Error as ErrorIcon,
  Loop as LoopIcon,
  Collections,
  PhotoLibraryOutlined,
  DescriptionOutlined,
  AssignmentOutlined,
  Compare,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { Project, Options as OptionsType, Photos, Detalles, Warning, NoConfigurables } from '../types';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import PhotoGallery from './PhotoGallery';
import PhotoSelectorGallery from './PhotoSelectorGallery';
import { analyzeImageQuality, QualityReport } from '../functions/qualityCheck';
import { defaultRetryPolicy } from '../utils/retryPolicy';
import { Loading } from './Loading';
import { captureException } from '../utils/captureException';

const rotate = keyframes`
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
`;

interface OptionsProps extends WithDeviceProps {
  project: Project;
  openOptions: boolean;
  handleClose: () => void;
  handleNextStep: (id: string) => void;
  setProject: (project: Project) => void;
  onOpenPdf: () => void;
  isSmallScreen: boolean;
  setPdfType: (pdfType: string) => void;
  refetch: () => void;
  handleCloseAll: () => void;
}

// Add this component before the Identification component (around line 61)
const NoTransition = React.forwardRef<any, any>(
  function NoTransition(props, ref) {
    const {
      children,
      appear,
      in: inProp,
      timeout,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      ownerState,
      ...rest
    } = props;
    // Return children directly without wrapper to preserve Dialog's layout
    return <>{children}</>;
  }
);

const Options: React.FC<OptionsProps> = ({
  project,
  openOptions,
  handleClose,
  handleNextStep,
  setProject,
  onOpenPdf,
  isSmallScreen,
  setPdfType,
  refetch,
  handleCloseAll,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [titles, setTitles] = useState<string[]>([]);
  const [titlesNoPhotos, setTitlesNoPhotos] = useState<string[]>([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<Photos[]>([]);
  const [selectedOpcion, setSelectedOpcion] = useState<string>('');
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(-1);
  const [uploading, setUploading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [qualityCheck, setQualityCheck] = useState<{
    open: boolean;
    report: QualityReport | null;
    file: File | null;
  }>({
    open: false,
    report: null,
    file: null,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const isEditable = project?.editable !== false;

  useEffect(() => {
    const checkOptionsNoFotos = async (
      allCompleted: "completed" | "pending",
      pendingTitles: string[] = [],
      pendingTitlesNoPhotos: string[] = []
    ) => {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        setError(t('capture.noTokenFound'));
        return;
      }
      let warning_opcionales: Warning[] = [];
      if (allCompleted === "completed") {
        warning_opcionales = [];
      } 
      else {
        pendingTitlesNoPhotos.forEach((title) => {
          warning_opcionales.push({id: title, message: 'Falta validar: ' + title, check: false});
        });
        pendingTitles.forEach((title) => {
          warning_opcionales.push({id: title, message: 'Falta validar: ' + title, check: false});
        });
      }
      axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        status_opcionales: allCompleted ,
        warning_opcionales: warning_opcionales,
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }).then(() => {
        setProject({
          ...project,
          status_opcionales: allCompleted,
          warning_opcionales: warning_opcionales,
        });
      }).catch((error) => {
        captureException(error, { tags: { feature: 'options' }, extra: { action: 'checkOptionsNoFotos', project_code: project?.project_code } });
        setError(error.response.data.error || t('capture.updateFailed'));
      });
    };

    let newTitles = [];
    let newTitlesNoPhotos = [];
    if (project) {
      // const sortedKeys = Object.keys(project).sort();
      const sortedKeys = project.keys_order ? project.keys_order.split(';') : Object.keys(project).sort();
      for (const key of sortedKeys) {
        if (key.startsWith("OPCIONALES")) {
          newTitles.push(key);
        }
        if (key.startsWith("DETALLES") && !key.includes("DE LA ORDEN")) {
          newTitlesNoPhotos.push(key);
        }
      }
      setTitles(newTitles);
      setTitlesNoPhotos(newTitlesNoPhotos);
    }
    if (project && newTitlesNoPhotos.length > 0 && newTitles.length > 0) {
      let allCompleted = "completed";
      let pendingTitles: string[] = [];
      let pendingTitlesNoPhotos: string[] = [];
      newTitlesNoPhotos.forEach((title) => {
        const detalles = project[title as keyof Project] as Detalles;
        if (!detalles) return;
        if (Object.values(detalles).some((detalle) => detalle.check === false || detalle.check === undefined)) {
          pendingTitlesNoPhotos.push(title);
        }
      });
      newTitles.forEach((title) => {
        const options = project[title as keyof Project] as OptionsType[];
        if (!options) return;
        if (options.some((option) => !(option.confidence && option.confidence >= 0.8 ? option.human_status ? option.human_status : option.ai_status : option.human_status))) {
          pendingTitles.push(title);
        }
      });
      if (pendingTitlesNoPhotos.length > 0 || pendingTitles.length > 0) {
        allCompleted = "pending";
      }
      if (allCompleted === "completed" && project.status_opcionales !== "completed") {
        checkOptionsNoFotos("completed");
      } else if (allCompleted === "pending" && project.status_opcionales !== "pending" || project.warning_opcionales.length  !== (pendingTitlesNoPhotos.length + pendingTitles.length)) {
        checkOptionsNoFotos("pending", pendingTitles, pendingTitlesNoPhotos);
      }
    }
  }, [project]);

  // Helper function to get main icon based on status (now Check/Close/Block)
  const getMainIcon = (isAiStatus: boolean, status: string | undefined, confidence: number | undefined) => {
    if (status === 'ok' || status === 'completed') {
      if (isAiStatus && confidence) {
        const confidenceNum = confidence;
        let color: 'success.main' | 'warning.main' | 'error.main' = 'success.main';
        
        if (confidenceNum < 0.8) {
          color = 'warning.main';
        }
        
        return { Icon: CheckCircleOutlineOutlined, color };
      } else {
        return { Icon: CheckCircleOutlineOutlined, color: 'success.main' as const };
      }
    } else if (status === 'ko') {
      return { Icon: CancelOutlined, color: 'error.main' as const };
    } else if (isAiStatus && status === 'na') {
      return { Icon: Block, color: 'primary.main' as const };
    } else if (isAiStatus) {
      return { Icon: RadioButtonUnchecked, color: 'warning.main' as const };
    }
    
    // Default case
    return { Icon: RadioButtonUnchecked, color: 'action.disabled' as const };
  };

  // Helper function to get secondary icon (now Memory/Person)
  const getSecondaryIcon = (isAiStatus: boolean) => {
    return isAiStatus ? <Memory sx={{ height: 16, width: 16 }} /> : <Person sx={{ height: 16, width: 16 }} />;
  };

  // Helper function to get detalles icon based on check status
  const getDetallesIcon = (check: boolean | undefined) => {
    if (check === false) {
      return { Icon: CancelOutlined, color: 'error.main' as const };
    } else if (check === true) {
      return { Icon: CheckCircleOutlineOutlined, color: 'success.main' as const };
    } else {
      return { Icon: RadioButtonUnchecked, color: 'action.disabled' as const };
    }
  };

  // console.log("selectedPhotos", selectedPhotos, project.fotos, galleryOpen);
  const handleViewPhoto = (fotos: Photos[] | undefined, title: string, option: string) => {
    // if (!fotos) return;
    setGalleryOpen(true);
    // setSelectedPhotos(fotos);
    setSelectedPhotos(fotos || []);
    setSelectedTitle(title);
    setSelectedOpcion(option);
  };

  const handlePhotoButtonClick = (event: React.MouseEvent<HTMLButtonElement>, opcion: string, title: string, index: number) => {
    event.stopPropagation();
    setSelectedOpcion(opcion);
    setSelectedTitle(title);
    setSelectedOptionIndex(index);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCapturePhoto = () => {
    handleMenuClose();
    cameraInputRef.current?.click();
  };

  const handleSelectGallery = () => {
    handleMenuClose();
    setSelectorOpen(true);
  };

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
  
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setError(t('errors.fileSelection.invalidFileType'));
      return;
    }
  
    setUploading(true);
    setError(null);
    
    try {
      // First, analyze image quality
      const qualityReport = await analyzeImageQuality(file);
      setUploading(false);
      
      // If quality is bad, prevent API call and show warning
      if (qualityReport.summary === 'bad') {
        setQualityCheck({
          open: true,
          report: qualityReport,
          file: file,
        });
        return;
      }
      
      // If quality is OK/warn or good, proceed with upload
      if (qualityReport.summary === 'ok') {
        setQualityCheck({
          open: true,
          report: qualityReport,
          file: file,
        });
        return; // User can decide to proceed or retake
      }
      
      // For good quality, proceed directly
      await proceedWithUpload(file);
      
    } catch (err) {
      captureException(err, { tags: { feature: 'options' }, extra: { action: 'analyzeImageQuality', project_code: project?.project_code } });
      console.error('Error analyzing image quality:', err);
      setError(t('errors.processing.failedAnalyzeQuality'));
      setUploading(false);
    }
  };

  const proceedWithUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    
    try {
      if (!user) throw new Error(t('errors.processing.userNotAuthenticated'));
      const idToken = await user.getIdToken();
  
      const formData = new FormData();
      formData.append('image', file, file.name);
      formData.append('project_code', project.project_code);
      formData.append('optional', selectedOpcion);
      formData.append('userId', user.uid);
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/add/image`;
  
      const response = await axios.post<{ id: string }>(apiUrl, formData, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
  
      if (response.data) {
        console.log("got image uploaded!");
        console.log(" response data", response.data);
        const updatedData = response.data as unknown as Project & { option_fotos?: Photos[] };
        
        
        const title = project[selectedTitle as keyof Project] as OptionsType[];
        // Create a completely new array with new object references
        const updatedTitle = title.map((option, idx) => 
          idx === selectedOptionIndex 
            ? { ...option, fotos: updatedData.option_fotos }
            : { ...option }
        );
        
        // Create updated project with option_fotos
        const updatedProject: Project = {
          ...project,
          fotos: updatedData.fotos,
          contributors: updatedData.contributors,
          lastModifiedBy: updatedData.lastModifiedBy,
          status: updatedData.status,
          lastModifiedAt: updatedData.lastModifiedAt,
          [selectedTitle as keyof Project]: updatedTitle,
        };
        
        setProject(updatedProject);
        await refetch();
        setSelectedTitle('');
        setSelectedOptionIndex(-1);
        setSelectedOpcion('');
      } else {
        throw new Error(t('errors.processing.noResultId'));
      }
    } catch (err) {
      captureException(err, { tags: { feature: 'options' }, extra: { action: 'proceedWithUpload', project_code: project?.project_code } });
      console.error('Error sending image:', err);
      let errorMessage = t('errors.processing.failed');
      if (axios.isAxiosError(err) && err.response) {
        errorMessage = `Error: ${err.response.data?.details || err.response.data?.error || err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setUploading(false);
      // setShowChoiceDialog(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleQualityCheckClose = () => {
    setQualityCheck({ open: false, report: null, file: null });
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleProceedWithQuality = async () => {
    if (qualityCheck.file) {
      handleQualityCheckClose();
      await proceedWithUpload(qualityCheck.file);
    }
  };

  const handlePhotoSelected = async (selectedPhoto: Photos) => {
    setUploading(true);
    setError(null);
    
    try {
      if (!user) throw new Error(t('errors.processing.userNotAuthenticated'));
      const idToken = await user.getIdToken();

      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/assign/image`;
      
      const response = await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.post(apiUrl, {
          foto_id: selectedPhoto.id,
          optional: selectedOpcion,
          project_code: project.project_code,
        }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Photo Reassignment API call', { 
        apiUrl, 
        userId: user.uid, 
        projectCode: project.project_code,
        photoId: selectedPhoto.id,
        newZone: selectedOpcion
      });

      if (response.data) {
        console.log('Photo reassigned successfully:', response.data);

        const updatedData = response.data as Photos[];
        
        const title = project[selectedTitle as keyof Project] as OptionsType[];
        const updatedTitle = title.map((option, idx) => 
          idx === selectedOptionIndex 
            ? { ...option, fotos: updatedData }
            : { ...option }
        );
        
        const updatedProject: Project = {
          ...project,
          [selectedTitle as keyof Project]: updatedTitle,
        };
        
        setProject(updatedProject);
        await refetch();
        setSelectedTitle('');
        setSelectedOptionIndex(-1);
        setSelectedOpcion('');
        setUploading(false);
      } else {
        throw new Error(t('errors.project.reassignmentFailed'));
      }
    } catch (err) {
      captureException(err, { tags: { feature: 'options' }, extra: { action: 'handlePhotoReassignment', project_code: project?.project_code } });
      console.error('Error reassigning photo:', err);
      let errorMessage = t('errors.project.photoReassignmentFailed');
      if (axios.isAxiosError(err) && err.response) {
        errorMessage = `Error: ${err.response.data?.details || err.response.data?.error || err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    }
  };

  const handleHumanStatusToggle = async (title: string, optionIndex: number) => {
    const projectOptions = project[title as keyof Project];
    if (Array.isArray(projectOptions)) {
      const currentStatus = projectOptions?.[optionIndex] as OptionsType;
      const newStatus = currentStatus?.human_status === undefined ? currentStatus.confidence && currentStatus.confidence >= 0.8 ? 'ko' : 'ok' : currentStatus?.human_status === 'ok' ? 'ko' : currentStatus?.human_status === 'ko' ? 'na' : 'ok';
      
      projectOptions[optionIndex] = {
        ...currentStatus,
        human_status: newStatus,
      };

      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }

      axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        [title as keyof Project]: projectOptions,
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }).then((response) => {
        console.log("response", response);
      }).catch((error) => {
        captureException(error, { tags: { feature: 'options' }, extra: { action: 'handleHumanStatusToggle', title, project_code: project?.project_code } });
        setError(error.response.data.error || t('errors.project.updateFailed'));
        projectOptions[optionIndex] = {
          ...currentStatus,
          human_status: currentStatus?.human_status,
        };
        setProject({
          ...project,
          [title as keyof Project]: projectOptions,
        });
      });

      setProject({
        ...project,
        [title as keyof Project]: projectOptions,
      });
    }
  };

  const handleNoConfigurablesToggle = async (index: number) => {
    const noConfigurables = project["NO CONFIGURABLES"] as NoConfigurables[];
    if (noConfigurables && noConfigurables[index]) {
      const currentCheck = noConfigurables[index].check;
      let newCheck: boolean | undefined;
      if (currentCheck == null) {
        newCheck = true;
      } else if (currentCheck === true) {
        newCheck = false;
      } else {
        newCheck = undefined;
      }

      let updatedNonConfigurables: NoConfigurables[] = noConfigurables.map((noConfigurable, idx) => 
        idx === index 
          ? { ...noConfigurable, check: newCheck }
          : { ...noConfigurable }
      );

      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        "NO CONFIGURABLES": updatedNonConfigurables,
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }).then((response) => {
        console.log("response", response);
        setProject({
          ...project,
          "NO CONFIGURABLES": updatedNonConfigurables,
        });
      }).catch((error) => {
        captureException(error, { tags: { feature: 'options' }, extra: { action: 'handleNoConfigurablesToggle', index, project_code: project?.project_code } });
        setError(error.response.data.error || t('errors.project.updateFailed'));
      });
    }
  };

  const handleDetallesToggle = async (title: string, key: string) => {
    const detalles = project[title as keyof Project] as Detalles;
    if (detalles && detalles[key]) {
      const currentCheck = detalles[key].check;
      let newCheck: boolean | undefined;
      
      if (currentCheck == null) {
        newCheck = true;
      } else if (currentCheck === true) {
        newCheck = false;
      } else {
        newCheck = undefined;
      }
      
      const updatedDetalles = {
        ...detalles,
        [key]: {
          ...detalles[key],
          check: newCheck,
        },
      };
      
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        [title as keyof Project]: updatedDetalles,
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }).then((response) => {
        console.log("response", response);
      }).catch((error) => {
        captureException(error, { tags: { feature: 'options' }, extra: { action: 'handleDetallesToggle', title, key, project_code: project?.project_code } });
        setError(error.response.data.error || t('errors.project.updateFailed'));
        const updatedDetalles = {
          ...detalles,
          [key]: {
            ...detalles[key],
            check: currentCheck,
          },
        };
        setProject({
          ...project,
          [title as keyof Project]: updatedDetalles,
        });
      });
      setProject({
        ...project,
        [title as keyof Project]: updatedDetalles,
      });
    }
  };

  const handleRecalculateOptions = async () => {
    const idToken = await user?.getIdToken();
    if (!idToken) {
      throw new Error('No idToken found');
    }
    setRecalculating(true);
    axios.post(`${process.env.REACT_APP_API_URL || ''}/api/recalculate/options`, 
        {
          project_code: project.project_code,
        }, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      }).then(async (response) => {
        console.log("response", response);
        await refetch();
        setRecalculating(false);
      }).catch((error) => {
        captureException(error, { tags: { feature: 'options' }, extra: { action: 'handleRecalculateOptions', project_code: project?.project_code } });
        console.error('Error recalculating options:', error);
        setError(error.response?.data?.error || t('errors.project.recalculationFailed'));
        setRecalculating(false);
      });
  };

  return (
    <Dialog
      fullScreen
      open={openOptions && project !== null}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        pt: isSmallScreen ? "56px" : "64px",
        pb: "56px",
      }}
      onClose={handleClose}
      slots={{ transition: NoTransition }}
      key={"options"}
    >
      <DialogTitle sx={{ p: 0, bgcolor: 'background.light' }}>
        {/* Appbar should be fixed on the top of the dialog */}
        <AppBar
          // position="static"
          color="inherit"
          position="fixed"
          sx={{
            // zIndex: (theme) => theme.zIndex.drawer,
            pt: isSmallScreen ? "56px" : "64px",
          }}
        >
          <Toolbar sx={{ p: 1 }}>
            <IconButton onClick={handleClose} color="inherit" sx={{ mr: 1 }}><ArrowBack/></IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }} />
            {/* <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('options.vehicleOptions')}</Typography> */}
            <Tooltip title={t('projectList.redoOptionals', { code: project?.project_code || '' })} placement="bottom">
              <IconButton 
                onClick={handleRecalculateOptions}
                disabled={recalculating || !isEditable}
                sx={{ 
                  mr: 1,
                  '& .MuiSvgIcon-root': {
                    animation: recalculating ? `${rotate} 2s linear infinite` : 'none',
                  },
                }}
              >
                <LoopIcon />
              </IconButton>
            </Tooltip>
            {project?.gcs_series_book_path && (
              <Tooltip title={t('projectList.seriesBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('series');
                }}
                sx={{ mr: 0.5 }}><Compare /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_homologation_book_path && (
              <Tooltip title={t('projectList.homologationBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('homologation');
                }}
                sx={{ mr: 0.5 }}><AssignmentOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_image_book_path && (
              <Tooltip title={t('projectList.imageBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('image_book');
                }}
                sx={{ mr: 0.5 }}><PhotoLibraryOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_report_path && (
              <Tooltip title={t('projectList.reportTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('report');
                }}
                sx={{ mr: 0.5 }}><DescriptionOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_pdf_path && (
              <Tooltip title={t('projectList.launchTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('lanzamiento');
                }}
                sx={{ mr: 0.5 }}><PictureAsPdf /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleCloseAll} color="inherit"><Close /></IconButton>
          </Toolbar>
        </AppBar>
          
        {recalculating && (
          <Box sx={{ p: 2, minHeight: "calc(100vh - 112px - " + (isSmallScreen ? "56px" : "72px") + ")", mt: isSmallScreen ? "56px" : "64px" }}>
            <Loading />
          </Box>
        )}
        {project && !recalculating && (
          <Box sx={{ py: 3, pl: 2, pr: 2, minHeight: "calc(100vh - 112px - " + (isSmallScreen ? "56px" : "72px") + ")", mt: isSmallScreen ? "56px" : "64px" }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Accordion sx={{ borderRadius: '8px !important', '& .MuiAccordionSummary-root': { margin: 0 } }}>
                <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{ 
                  '&.Mui-expanded': { margin: 0 }, 
                  '& .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: "0px !important" },
                  '&.Mui-expanded .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: "0px !important" }
                }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2, fontSize: 18 }}>{t('options.vehicleDetails')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {titlesNoPhotos
                  .filter(title => project[title as keyof Project] != null)
                  .map((title) => (
                    <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 0.5,
                        mt: 2.5,
                        fontSize: 18,
                      }}
                    >
                      {title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()}
                    </Typography>
                    
                    <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, pb: 0, pt: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.07)',}}>
                      
                      {Object.entries(project[title as keyof Project] as Detalles).map(([key, value], index) => (
                        <ListItem
                          key={index}
                          sx={{
                            borderBottom: index < Object.entries(project[title as keyof Project] as Detalles).length - 1 ? '1px solid #E0E0E0' : 'none',
                            // pl: 1,
                            // pr: 8,
                            // show elements in columns
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            gap: 1,
                            minHeight: 56,
                          }}
                          secondaryAction={
                            <IconButton
                            edge="end"
                            sx={{ m: 0 }}
                            disabled={!isEditable}
                            onClick={() => handleDetallesToggle(title, key)}
                            >
                              {(() => {
                                const { Icon, color } = getDetallesIcon(value?.check);
                                return (
                                  <Icon
                                    sx={{ height: 32, width: 32, color: color }}
                                  />
                                );
                              })()}
                            </IconButton>
                          }
                        >
                          <ListItemText 
                            primary={key} 
                            secondary={
                              (() => {
                                const val = value?.value;
                                if (val === null || val === undefined) return '';
                                if (typeof val === 'object') {
                                  // If it's an object, try to format it nicely
                                  const keys = Object.keys(val);
                                  if (keys.length > 0) {
                                    // Try to show values if they're strings, otherwise show keys
                                    const values = keys.map(k => {
                                      const v = val[k];
                                      return typeof v === 'string' ? `${k}: ${v}` : k;
                                    });
                                    return values.join(', ');
                                  }
                                  return JSON.stringify(val);
                                }
                                return String(val);
                              })()
                            } 
                            sx={{ mr: 3 }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  ))}
                  {project["NO CONFIGURABLES"] && project["NO CONFIGURABLES"].length > 0 && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 0.5,
                        mt: 2.5,
                        fontSize: 18,
                      }}
                    >
                      No Configurables
                    </Typography>
                    <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, pb: 0, pt: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.07)',}}>
                      
                      {project["NO CONFIGURABLES"].map((noConfigurable, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            borderBottom: index < project["NO CONFIGURABLES"]!.length - 1 ? '1px solid #E0E0E0' : 'none',
                            // pl: 1,
                            // pr: 8,
                            // show elements in columns
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            gap: 1,
                            minHeight: 56,
                          }}
                          secondaryAction={
                            <IconButton
                            edge="end"
                            sx={{ m: 0 }}
                            disabled={!isEditable}
                            onClick={() => handleNoConfigurablesToggle(index)}
                            >
                              {(() => {
                                const { Icon, color } = getDetallesIcon(noConfigurable?.check);
                                return (
                                  <Icon
                                    sx={{ height: 32, width: 32, color: color }}
                                  />
                                );
                              })()}
                            </IconButton>
                          }
                        >
                          <ListItemText 
                            primary={noConfigurable?.["Nº SR"] || noConfigurable?.["N° SR"]} 
                            secondary={
                              (() => {
                                const val = noConfigurable?.["Nombre"] + ": " + noConfigurable?.["Descripción"];
                                if (val === null || val === undefined) return '';
                                if (typeof val === 'object') {
                                  // If it's an object, try to format it nicely
                                  const keys = Object.keys(val);
                                  if (keys.length > 0) {
                                    // Try to show values if they're strings, otherwise show keys
                                    const values = keys.map(k => {
                                      const v = val[k];
                                      return typeof v === 'string' ? `${k}: ${v}` : k;
                                    });
                                    return values.join(', ');
                                  }
                                  return JSON.stringify(val);
                                }
                                return String(val);
                              })()
                            } 
                            sx={{ mr: 3 }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                    </Box>
                    )}
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 }, '& .MuiAccordionDetails-root': { paddingTop: 0 }, '& .MuiAccordionSummary-root': { margin: 0 }, borderRadius: '8px !important' }}>
                <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{ 
                  '&.Mui-expanded': { margin: 0 }, 
                  '& .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: "0px !important" },
                  '&.Mui-expanded .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: "0px !important" }
                }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2, fontSize: 18 }}>{t('options.vehicleOptions')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {titles.map((title) => (
                    title.startsWith("OPCIONALES")  && project[title as keyof Project] != null && (
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 0.5,
                          mt: 2.5,
                          fontSize: 18,
                        }}
                      >
                        {title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()}
                      </Typography>
                      
                      <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, pb: 0, pt: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.07)',}}>
                        {title in project && Array.isArray(project[title as keyof Project]) && (project[title as keyof Project] as OptionsType[]).map((option: OptionsType, index: number) => (
                          <ListItem
                            key={`${title}-${option.n_opcion}-${index}`}
                            sx={{
                              borderBottom: index < (project[title as keyof Project] as OptionsType[]).length - 1 ? '1px solid #E0E0E0' : 'none',
                              // pl: 1,
                              // pr: 8,
                              // show elements in columns
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <ListItemText primary={option.descripcion} secondary={option.n_opcion || ''} />
                              {option.in_lista === false && <Tooltip title={t('options.notInOptionalsList')}><ErrorIcon sx={{ color: 'primary.main', mt: "6px", mr: "8px" }} /></Tooltip>}
                            </Box>
                            {/* display box elements in the same line, filling all the space */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              {/* AI Status Icon with secondary overlay */}
                              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <IconButton 
                                  aria-label="ai-status" 
                                  disabled 
                                  sx={{ 
                                    cursor: 'default',
                                    '&.Mui-disabled': {
                                      opacity: 1,
                                      '& .MuiIconButton-root': {
                                        opacity: 1,
                                      },
                                      '& .MuiSvgIcon-root': {
                                        opacity: 1,
                                      },
                                    },
                                  }}
                                >
                                  {(() => {
                                    const { Icon, color } = getMainIcon(true, option.ai === true ? option.ai_status : "na", option.confidence);
                                    return (
                                      <Icon 
                                        sx={{ 
                                          height: 32, 
                                          width: 32,
                                          color: color,
                                        }} 
                                      />
                                    );
                                  })()}
                                </IconButton>
                                <Box sx={{ 
                                  position: 'absolute',
                                  top: -3,
                                  right: -3,
                                  backgroundColor: 'transparent',
                                  borderRadius: '50%',
                                  padding: 0.1,
                                }}>
                                  {getSecondaryIcon(true)}
                                </Box>
                              </Box>

                              {/* Human Status Icon with secondary overlay */}
                              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <IconButton aria-label="human-status" disabled={!isEditable} onClick={() => handleHumanStatusToggle(title, index)}>
                                  {(() => {
                                    const { Icon, color } = getMainIcon(false,
                                      option.confidence && option.confidence >= 0.8 ? option.human_status ? option.human_status : option.ai_status : option.human_status,
                                      option.confidence);
                                    return (
                                      <Icon 
                                        sx={{ 
                                          height: 32, 
                                          width: 32,
                                          color: color,
                                        }} 
                                      />
                                    );
                                  })()}
                                </IconButton>
                                <Box sx={{ 
                                  position: 'absolute',
                                  top: -3,
                                  right: -3,
                                  backgroundColor: 'transparent',
                                  borderRadius: '50%',
                                  padding: 0.1,
                                }}>
                                  {getSecondaryIcon(false)}
                                </Box>
                              </Box>

                              {option.fotos && option.fotos.length > 0 ? (
                                <IconButton
                                  sx={{ height: 48, width: 48 }}
                                  aria-label="photos"
                                  onClick={() => handleViewPhoto(option.fotos, title, option.n_opcion)}
                                >
                                  {option.n_opcion === selectedOpcion && uploading ? (
                                    <CircularProgress size={32} />
                                  ) : (
                                    <Image sx={{ height: 32, width: 32, color: 'success.main' }} />
                                  )}
                                </IconButton>
                              ) : isEditable ? (
                                <IconButton
                                  sx={{ height: 48, width: 48 }}
                                  aria-label="photos"
                                  disabled={option.n_opcion === selectedOpcion && uploading}
                                  onClick={(event) => handlePhotoButtonClick(event, option.n_opcion, title, index)}
                                >
                                  {option.n_opcion === selectedOpcion && uploading ? (
                                    <CircularProgress size={32} />
                                  ) : (
                                    <PhotoCamera
                                      sx={{
                                        height: 32,
                                        width: 32,
                                        color: option.grupo ? 'warning.main' : 'primary.main',
                                      }}
                                    />
                                  )}
                                </IconButton>
                              ) : (
                                // Placeholder para mantener alineación
                                <Box sx={{ width: 48, height: 48 }} />
                              )}
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                    )
                    ))}
                </AccordionDetails>
              </Accordion>

              <Button variant="contained" color="primary" size="large" onClick={() => handleNextStep('vehicle-options')}>{t('capture.vehicle.next')}</Button>

              <input
                type="file"
                accept="image/*"
                hidden
                ref={cameraInputRef}
                onChange={handleFileSelected}
                onClick={(event) => event.stopPropagation()}
                capture="environment"
              />
            </Box>
          </Box>
        )}
      </DialogTitle>

      <PhotoGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        photos={selectedPhotos}
        initialPhotoType={selectedPhotos[0]?.zone}
        isMobile={isSmallScreen}
        enableAddPhoto={true}
        projectCode={project.project_code}
        zone={selectedOpcion}
        multiple={true}
        isOption={true}
        title={selectedTitle}
        option={selectedOpcion}
        project={project}
        onPhotoDeleted={async () => {
          // Refetch project data after photo is added
          try {
            const idToken = await user?.getIdToken();
            if (!idToken) return;
            
            const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
              headers: { 'Authorization': `Bearer ${idToken}` },
            });
            
            if (response.data) {
              setProject(response.data);
              console.log("setting data", response.data);
              // Refresh selected photos
              const updatedPhotos = response.data[selectedTitle as keyof Project]?.find((option: OptionsType) =>
                option.n_opcion === selectedOpcion)?.fotos || [];
              setSelectedPhotos(updatedPhotos);
              if (updatedPhotos.length === 0) {
                setGalleryOpen(false);
              }
              console.log("setting updated photos", updatedPhotos);
            }
          } catch (err) {
            captureException(err, { tags: { feature: 'options' }, extra: { action: 'refetchProject', project_code: project?.project_code } });
            console.error('Error refetching project:', err);
            // Don't clear photos on error - keep the existing ones
          }
        }}
      />

      <PhotoSelectorGallery
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelectPhoto={handlePhotoSelected}
        project={project}
        currentZone={selectedOpcion}
        isMobile={isSmallScreen}
      />

      {/* Photo Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCapturePhoto}>
          <PhotoCamera sx={{ mr: 1, color: 'primary.main' }} />
          {t('options.capturePhoto')}
        </MenuItem>
        <MenuItem onClick={handleSelectGallery}>
          <Collections sx={{ mr: 1, color: 'primary.main' }} />
          {t('options.selectGallery')}
        </MenuItem>
      </Menu>

      {/* Quality Check Dialog */}
      <Dialog
        open={qualityCheck.open}
        onClose={handleQualityCheckClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('qualityCheck.title')}
        </DialogTitle>
        <DialogContent>
          {qualityCheck.report && (
            <Box sx={{ mb: 2 }}>
              <Alert 
                sx={{ borderRadius: '0px !important', mb: 2 }}
                severity={qualityCheck.report.summary === 'bad' ? 'error' : 'warning'}
              >
                <Typography variant="body2">
                  {qualityCheck.report.summary === 'bad'
                    ? t('qualityCheck.poorQuality', { score: qualityCheck.report.score })
                    : t('qualityCheck.acceptableQuality', { score: qualityCheck.report.score })
                  }
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleQualityCheckClose} color="primary">
            {t('qualityCheck.retakePhoto')}
          </Button>
          {qualityCheck.report && qualityCheck.report.summary !== 'bad' && (
            <Button onClick={handleProceedWithQuality} variant="contained" color="primary">
              {t('qualityCheck.useAnyway')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* the alert should be on the top of the dialog at the bottom position. Should have a close alert button, placed at the end of the alert */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 200, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {error}
          <IconButton onClick={() => setError(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
    </Dialog>
  );
};

export default withDevice(React.memo(Options));
